/* eslint-disable react/no-find-dom-node */

import '@atlassian/jira-vendor/src/vendor-parcel.tsx';
import '@atlassian/jira-vendor/src/commons-parcel.tsx';

// eslint-disable-next-line jira/restricted/react
import React, { PureComponent } from 'react';
import { findDOMNode, render, unmountComponentAtNode } from 'react-dom';
import { styled } from '@compiled/react';
import debounce from 'lodash/debounce';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { JiraContextContainer } from '@atlassian/jira-context-container/src/JiraContextContainer.tsx';
import type { AppProps } from '@atlassian/jira-global-issue-navigator/src/view/types.tsx';
import GlobalIssueNavigatorIssueApp from '@atlassian/jira-global-issue-navigator/src/view/view.tsx';
import type { DispatchExternalAction } from '@atlassian/jira-issue-view-store/src/actions/external-actions.tsx';
import { listenToExternalActionFromLegacy } from '@atlassian/jira-issue-view-store/src/actions/legacy-external-action.tsx';
import { getDataProvider } from '@atlassian/jira-providers-issue/src/service/index.tsx';
import { StrictMode } from '@atlassian/jira-react-strict-mode/src/StrictMode.tsx';
import { toIssueKey, type IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import global from '../../global.tsx';
import setupIssuesCache from '../common/issues-cache-setup.tsx';
import { safeGetAJS$DomNode } from './util/ajs-safe.tsx';

const dataProvider = getDataProvider();
let issueKey: IssueKey;
let isInitialLoad = true;

// We need a node in the DOM that does not get repainted by the monolith (Marionette) when changing issue

// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
const container = document.body;

const $container = safeGetAJS$DomNode(container);

type EventData = {
	issueId: string;
	issueKey: string;
};

const sendEventToLegacyCode = (eventName: string, eventData: EventData) => {
	$container && $container.trigger(eventName, eventData);
};

const parseIssueKey = () => {
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const pathName = window.location.pathname.split('/');
	return toIssueKey(pathName[pathName.length - 1]);
};

const getIssueViewElement = (): HTMLElement | null =>
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	document.getElementById('new-issue-body-container');

// eslint-disable-next-line jira/react/no-class-components
class IssueView extends PureComponent<AppProps> {
	render() {
		return (
			<StrictMode>
				<IssueContainer>
					<JiraContextContainer>
						<GlobalIssueNavigatorIssueApp {...this.props} />
					</JiraContextContainer>
				</IssueContainer>
			</StrictMode>
		);
	}
}

//  trying to fix this opens too many cans of worms
let dispatchExternalAction: DispatchExternalAction | null | undefined;

let oldApp: ReturnType<typeof render> | null = null;

const renderApp = () => {
	const issueViewElement = getIssueViewElement();

	const appProps: AppProps = {
		issueKey,
		isLoadedWithPage: true,
		onIssueAppReady: () => {
			if (isInitialLoad) {
				isInitialLoad = false;
			}
		},
		onDispatchExternalActionRef: (ref) => {
			dispatchExternalAction = ref;
		},
		onIssueDeleteSuccess: ({ issueId, issueKey: deletedIssueKey }) => {
			sendEventToLegacyCode('remove-issue-from-search-list', {
				issueId,
				issueKey: deletedIssueKey,
			});
		},
	};

	if (issueViewElement) {
		if (oldApp) {
			// Unmount the previous app to avoid memory leak.
			// Unmounting right before the app is re-rendered to make sure prev app is cleaned up regardless of where issue view is rendered in GIN.
			// @ts-expect-error - TS2345 - Argument of type 'ParentNode | null | undefined' is not assignable to parameter of type 'Element | DocumentFragment'. | TS7005 - Variable 'oldApp' implicitly has an 'any' type.
			unmountComponentAtNode(findDOMNode(oldApp)?.parentNode);
		}
		oldApp = render(<IssueView {...appProps} />, issueViewElement);
	}
};

global.install();

$container.on(
	'new-issue-body-container-for-gin-rendered',
	debounce(() => {
		issueKey = parseIssueKey();
		setupIssuesCache(dataProvider, '.issue-list > li', 'data-key');
		renderApp();
	}, 0),
);

// If we are injecting this fragment when loading the list view, we set isInitialLoad to false,
// so that the first load of the issue view won't be considered initial
$container.on('global-issue-navigator-list-view-rendered', () => {
	isInitialLoad = false;
	// We want to wait for the next tick before setting up the cache.
	// This will allow Marionette to finish painting the page.
	// If we do not wait, the IntersectionObserver will not be able to compute intersections correctly.
	setTimeout(() => setupIssuesCache(dataProvider, '#issuetable tr.issuerow', 'data-issuekey'));
});

listenToExternalActionFromLegacy(() => ({
	dispatchExternalAction,
	modifiedIssueIssueKey: issueKey,
}));

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const IssueContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	marginLeft: `${-gridSize * 2.25}px`,
	height: '100%',
});
